import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, ButtonBase, Collapse, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography,
} from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import { useAuthStore } from '../../store/authStore';
import { useSalonContextStore } from '../../store/salonContextStore';
import { DISPLAY_FONT, PREMIUM_COLORS, tokens } from '../../theme';
import { findActivePath, NavGroup, NavPinned, resolveNav } from './navConfig';
import { initials } from '../../utils/format';

const sb = PREMIUM_COLORS.sidebar;

const itemBaseSx = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  minHeight: 38,
  px: 1.5,
  borderRadius: '10px',
  color: sb.text,
  fontFamily: 'inherit',
  fontSize: '0.90625rem',
  fontWeight: 550,
  textAlign: 'left' as const,
  justifyContent: 'flex-start',
  transition: 'background-color .15s ease, color .15s ease',
  '& .nav-icon': { display: 'flex', color: 'inherit', '& .MuiSvgIcon-root': { fontSize: 21 } },
  '&:hover': { bgcolor: sb.hoverBg, color: '#EDE8F4' },
  '&.Mui-focusVisible': { outline: `2px solid ${sb.accent}`, outlineOffset: -2 },
};

const activeSx = {
  bgcolor: sb.active,
  color: sb.textActive,
  '& .nav-icon': { display: 'flex', color: sb.accent, '& .MuiSvgIcon-root': { fontSize: 21 } },
  '&:hover': { bgcolor: sb.active, color: sb.textActive },
};

export const Monogram: React.FC<{ size?: number }> = ({ size = 36 }) => (
  <Box
    aria-hidden
    sx={{
      width: size, height: size, borderRadius: `${Math.round(size * 0.28)}px`, flexShrink: 0,
      display: 'grid', placeItems: 'center',
      bgcolor: '#2A2038', border: `1px solid ${'rgba(201,164,106,0.45)'}`,
      fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: size * 0.6, lineHeight: 1, color: sb.accent,
      fontVariationSettings: '"opsz" 11',
    }}
  >
    V
  </Box>
);

/* ---------------- przelacznik salonu ---------------- */

const SalonSwitcher: React.FC<{ collapsed: boolean; onNavigate?: () => void }> = ({ collapsed, onNavigate }) => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const { salons, activeSalonId, setActiveSalonId, loading } = useSalonContextStore();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = salons.find((s) => s.id === activeSalonId) as ({ id: string; name: string; logoUrl?: string | null; city?: string | null } | undefined);
  const name = active?.name || (loading ? 'Ładowanie…' : 'Brak salonu');
  const isOwner = role === 'SALON_OWNER';

  const avatar = (
    <Avatar
      variant="rounded"
      src={active?.logoUrl || undefined}
      sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: '#3A2E52', color: '#EDE6F7', fontSize: '0.8125rem', fontWeight: 700 }}
    >
      {initials(active?.name?.split(' ')[0], active?.name?.split(' ')[1], active?.name)}
    </Avatar>
  );

  return (
    <>
      <Tooltip title={collapsed ? `${name} — zmień salon` : ''} placement="right">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Zmień salon"
          aria-haspopup="menu"
          sx={{
            width: '100%', borderRadius: '12px', p: collapsed ? 0.5 : 1.25, gap: 1.25, justifyContent: collapsed ? 'center' : 'flex-start',
            bgcolor: collapsed ? 'transparent' : 'rgba(255,255,255,0.04)',
            border: collapsed ? 'none' : '1px solid rgba(255,255,255,0.07)',
            textAlign: 'left',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
            '&.Mui-focusVisible': { outline: `2px solid ${sb.accent}`, outlineOffset: -2 },
          }}
        >
          {avatar}
          {!collapsed && (
            <>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ color: '#F4F0F9', fontSize: '0.875rem', fontWeight: 650, lineHeight: 1.3 }}>{name}</Typography>
                <Typography noWrap sx={{ color: sb.textDim, fontSize: '0.78125rem', lineHeight: 1.35 }}>
                  {salons.length > 1 ? `${salons.length} salony · zmień` : isOwner ? 'Zarządzaj salonami' : 'Twój salon'}
                </Typography>
              </Box>
              <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: sb.textDim }} />
            </>
          )}
        </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={collapsed ? { vertical: 'top', horizontal: 'right' } : { vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={collapsed ? { vertical: 'top', horizontal: 'left' } : { vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 240, ml: collapsed ? 1 : 0 } } }}
      >
        <Typography sx={{ px: 1.5, pt: 0.75, pb: 0.5, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.muted }}>
          Salony
        </Typography>
        {salons.map((s) => (
          <MenuItem
            key={s.id}
            selected={s.id === activeSalonId}
            onClick={() => { setActiveSalonId(s.id); setAnchor(null); }}
          >
            <ListItemText primary={s.name} primaryTypographyProps={{ noWrap: true }} />
            {s.id === activeSalonId && <CheckRoundedIcon sx={{ fontSize: 18, color: tokens.plum, ml: 1 }} />}
          </MenuItem>
        ))}
        {salons.length === 0 && <MenuItem disabled>Brak salonów</MenuItem>}
        {isOwner && <Divider sx={{ my: 0.5 }} />}
        {isOwner && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/salons'); onNavigate?.(); }}>
            <ListItemIcon><StorefrontOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Zarządzaj salonami" />
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/salons/new'); onNavigate?.(); }}>
            <ListItemIcon><AddBusinessOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Dodaj salon" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/* ---------------- pozycje ---------------- */

const PinnedItem: React.FC<{ item: NavPinned; active: boolean; collapsed: boolean; onNavigate?: () => void; badge?: number }> = ({ item, active, collapsed, onNavigate, badge }) => (
  <Tooltip title={collapsed ? item.label : ''} placement="right">
    <ButtonBase
      component={RouterLink}
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      sx={{ ...itemBaseSx, ...(collapsed ? { justifyContent: 'center', px: 0, width: 44, mx: 'auto' } : {}), ...(active ? activeSx : {}) }}
    >
      <Box className="nav-icon">{item.icon}</Box>
      {!collapsed && <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</Box>}
      {!collapsed && !!badge && (
        <Box component="span" sx={{ ml: 'auto', minWidth: 20, px: 0.75, lineHeight: '19px', borderRadius: 999, bgcolor: sb.badge, color: tokens.ink, fontSize: '0.75rem', fontWeight: 700, textAlign: 'center' }}>
          {badge}
        </Box>
      )}
    </ButtonBase>
  </Tooltip>
);

const GroupSection: React.FC<{
  group: NavGroup;
  open: boolean;
  activePath: string | null;
  containsActive: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
}> = ({ group, open, activePath, containsActive, collapsed, onToggle, onNavigate }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const listId = `nav-group-${group.id}`;

  if (collapsed) {
    return (
      <>
        <Tooltip title={anchor ? '' : group.label} placement="right">
          <ButtonBase
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label={group.label}
            aria-haspopup="menu"
            sx={{ ...itemBaseSx, justifyContent: 'center', px: 0, width: 44, mx: 'auto', position: 'relative', ...(containsActive ? activeSx : {}) }}
          >
            <Box className="nav-icon">{group.icon}</Box>
          </ButtonBase>
        </Tooltip>
        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { ml: 1.25, minWidth: 220 } } }}
        >
          <Typography sx={{ px: 1.5, pt: 0.75, pb: 0.5, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.muted }}>
            {group.label}
          </Typography>
          {group.items.map((it) => (
            <MenuItem
              key={it.path}
              selected={it.path === activePath}
              onClick={() => { setAnchor(null); navigate(it.path); onNavigate?.(); }}
            >
              {it.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  return (
    <Box>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={listId}
        sx={{ ...itemBaseSx, ...(open ? { color: '#F4F0F9' } : {}) }}
      >
        <Box className="nav-icon" sx={containsActive && !open ? { color: `${sb.accent} !important` } : undefined}>{group.icon}</Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.label}</Box>
        {containsActive && !open && (
          <Box component="span" aria-label="zawiera bieżącą stronę" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: sb.accent, flexShrink: 0 }} />
        )}
        <ChevronRightRoundedIcon
          sx={{ fontSize: 18, color: sb.textDim, transition: 'transform .18s ease', transform: open ? 'rotate(90deg)' : 'none', flexShrink: 0 }}
        />
      </ButtonBase>
      <Collapse in={open} timeout={180} unmountOnExit={false}>
        <Box
          component="ul"
          id={listId}
          sx={{ listStyle: 'none', m: 0, mt: 0.25, mb: 0.75, ml: '22px', pl: '12px', borderLeft: '1px solid rgba(255,255,255,0.09)' }}
        >
          {group.items.map((it) => {
            const active = it.path === activePath;
            return (
              <Box component="li" key={it.path}>
                <ButtonBase
                  component={RouterLink}
                  to={it.path}
                  onClick={onNavigate}
                  tabIndex={open ? 0 : -1}
                  aria-current={active ? 'page' : undefined}
                  sx={{
                    width: '100%', justifyContent: 'flex-start', textAlign: 'left',
                    px: 1.25, py: 0.75, my: '1px', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.875rem', fontWeight: active ? 650 : 500,
                    color: active ? '#FFFFFF' : sb.text,
                    bgcolor: active ? sb.active : 'transparent',
                    position: 'relative',
                    '&:hover': { bgcolor: active ? sb.active : sb.hoverBg, color: '#EDE8F4' },
                    '&.Mui-focusVisible': { outline: `2px solid ${sb.accent}`, outlineOffset: -2 },
                    ...(active && {
                      '&::before': { content: '""', position: 'absolute', left: -13, top: 8, bottom: 8, width: '1px', bgcolor: sb.accent },
                    }),
                  }}
                >
                  {it.label}
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ---------------- pasek ---------------- */

interface SidebarNavProps {
  collapsed: boolean;
  onNavigate?: () => void;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ collapsed, onNavigate }) => {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const nav = useMemo(() => resolveNav(user?.role), [user?.role]);
  const { path: activePath, groupId: activeGroupId } = findActivePath(location.pathname, nav);
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroupId);

  // Grupa biezacej strony otwiera sie sama przy kazdej zmianie strony.
  useEffect(() => {
    if (activeGroupId) setOpenGroup(activeGroupId);
  }, [activeGroupId, location.pathname]);

  const showSalon = user && (user.role === 'SALON_OWNER' || user.role === 'STAFF');

  return (
    <Box
      component="nav"
      aria-label="Menu główne"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: sb.text,
        background: `radial-gradient(120% 50% at 0% 0%, rgba(201,164,106,0.09), transparent 55%), linear-gradient(180deg, #1A1424 0%, #15101C 100%)`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: collapsed ? 1 : 2, pt: 1.75, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5, flexShrink: 0 }}>
        <ButtonBase
          component={RouterLink}
          to="/dashboard"
          onClick={onNavigate}
          aria-label="Visitio — Panel"
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: collapsed ? 'center' : 'flex-start', px: collapsed ? 0 : 0.75, py: 0.25, borderRadius: '10px', '&.Mui-focusVisible': { outline: `2px solid ${sb.accent}` } }}
        >
          <Monogram />
          {!collapsed && (
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '1.4375rem', color: '#F4F0F9', letterSpacing: '-0.01em', fontVariationSettings: '"opsz" 48' }}>
              Visitio
            </Typography>
          )}
        </ButtonBase>
        {showSalon && <SalonSwitcher collapsed={collapsed} onNavigate={onNavigate} />}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: collapsed ? 1 : 1.5,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.12)', backgroundClip: 'content-box' },
        }}
      >
        {nav.pinned.map((item) => (
          <PinnedItem key={item.path} item={item} active={item.path === activePath} collapsed={collapsed} onNavigate={onNavigate} />
        ))}

        {(nav.groups.length > 0 || nav.singles.length > 0) && (
          collapsed ? (
            <Box sx={{ height: '1px', bgcolor: sb.divider, mx: 1, my: 1 }} />
          ) : (
            <Typography sx={{ px: 1.5, pt: 1.75, pb: 0.5, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: sb.textDim }}>
              Obszary
            </Typography>
          )
        )}

        {nav.singles.map((item) => (
          <PinnedItem key={item.path} item={item} active={item.path === activePath} collapsed={collapsed} onNavigate={onNavigate} />
        ))}

        {nav.groups.map((g) => (
          <GroupSection
            key={g.id}
            group={g}
            open={openGroup === g.id}
            activePath={activePath}
            containsActive={activeGroupId === g.id}
            collapsed={collapsed}
            onToggle={() => setOpenGroup((cur) => (cur === g.id ? null : g.id))}
            onNavigate={onNavigate}
          />
        ))}
      </Box>

    </Box>
  );
};

export default SidebarNav;
